import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getOrderById } from '../services/orderService';
import './OrderConfirmationPage.css';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { clearCart } = useCart();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!orderId) {
          setError('Order ID not found');
          return;
        }

        const orderData = await getOrderById(orderId);
        if (!orderData) {
          setError('Order not found');
          return;
        }

        // Verify user access (user can only view their own orders)
        if (currentUser && orderData.userId !== currentUser.uid && orderData.userId !== 'guest') {
          setError('Unauthorized access to order');
          return;
        }

        setOrder(orderData);
        
        // Clear cart after successful order confirmation
        clearCart();
        
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, currentUser, clearCart]);

  const formatPrice = (price) => {
    return `₹${price.toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-confirmation-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">✅</div>
          <h1>Order Confirmed!</h1>
          <p>Thank you for your purchase. Your order has been successfully placed.</p>
        </div>

        {/* Order Details */}
        <div className="order-details-card">
          <div className="order-header">
            <h2>Order Details</h2>
            <div className="order-meta">
              <span className="order-id">Order ID: {order.orderId}</span>
              <span className="order-date">Placed on: {formatDate(order.createdAt)}</span>
            </div>
          </div>

          {/* Payment Information */}
          <div className="payment-info">
            <h3>Payment Information</h3>
            <div className="payment-details">
              <div className="payment-row">
                <span>Payment ID:</span>
                <span>{order.paymentId}</span>
              </div>
              <div className="payment-row">
                <span>Payment Status:</span>
                <span className="status-badge success">{order.paymentStatus}</span>
              </div>
              <div className="payment-row">
                <span>Payment Method:</span>
                <span>{order.paymentMethod}</span>
              </div>
              <div className="payment-row total-row">
                <span>Total Amount:</span>
                <span className="amount" style={{ color: '#0063B8', fontSize: '32px', fontWeight: '800' }}>
                  {formatPrice(order.totalAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="order-items">
            <h3>Items Ordered ({order.items.length})</h3>
            <div className="items-list">
              {order.items.map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-image">
                    {item.imageUrls && item.imageUrls.length > 0 ? (
                      <img src={item.imageUrls[0]} alt={item.name} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="item-details">
                    <h4>{item.name}</h4>
                    <p className="item-brand">{item.brand}</p>
                    {item.selectedRAM && (
                      <p className="item-spec">RAM: {item.selectedRAM.size}</p>
                    )}
                    {item.selectedStorage && (
                      <p className="item-spec">Storage: {item.selectedStorage.size}</p>
                    )}
                    <div className="item-pricing">
                      <span className="quantity">Qty: {item.quantity}</span>
                      <span className="price">{formatPrice(item.price)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <div className="shipping-info">
              <h3>Shipping Address</h3>
              <div className="address-details">
                <p><strong>{order.shippingAddress.name}</strong></p>
                <p>{order.shippingAddress.phone}</p>
                <p>{order.shippingAddress.addressLine1}</p>
                {order.shippingAddress.addressLine2 && (
                  <p>{order.shippingAddress.addressLine2}</p>
                )}
                <p>
                  {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
                </p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>
          )}

          {/* Order Status */}
          <div className="order-status">
            <h3>Order Status</h3>
            <div className="status-info">
              <div className="status-row">
                <span>Order Status:</span>
                <span className="status-badge confirmed">{order.orderStatus}</span>
              </div>
              <div className="status-row">
                <span>Fulfillment Status:</span>
                <span className="status-badge processing">{order.fulfillmentStatus}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            onClick={() => navigate('/orders')} 
            className="btn-primary"
          >
            View All Orders
          </button>
          <button 
            onClick={() => navigate('/')} 
            className="btn-secondary"
          >
            Continue Shopping
          </button>
        </div>

      </div>
    </div>
  );
};

export default OrderConfirmationPage;